import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { listIntrospectionRuns, IntrospectionRun } from '../Connections/api';
import { useSimpleSidebar } from '../../shell/simpleSidebarState';
import { AgentAbout, getPipelineAgents, getRouteUsage, PipelineAgents, UsageWeek } from './pipelineAgentsApi';

export type AgentTab = 'data' | 'discover' | 'catalog';

const TABS: Array<{ id: AgentTab; label: string }> = [
  { id: 'data', label: 'Data agent' }, { id: 'discover', label: 'Discover agent' }, { id: 'catalog', label: 'Catalog agent' },
];

/** The routes whose asks each agent answers (usage is counted per route, S15). */
export const ROUTES_OF: Record<'data' | 'catalog', Array<{ route: string; label: string }>> = {
  data: [{ route: 'data', label: 'Data questions' }, { route: 'why', label: 'Why questions' }],
  catalog: [{ route: 'catalog', label: 'Catalog questions' }],
};

const n = (v: number) => v.toLocaleString('en-US');
const usd = (v: number) => `$${v.toFixed(2)}`;
const secs = (v: number) => `${Number.isInteger(v) ? v : v.toFixed(1)} s`;
const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—');
const weekOf = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const boxSx = { border: '1px solid var(--line)', borderRadius: '14px', overflow: 'hidden', bgcolor: 'var(--bg)' };
const headSx = { display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.5, borderBottom: '1px solid var(--line-2)' };
const thSx = { textAlign: 'left' as const, fontWeight: 500, color: 'var(--ink-3)', fontSize: 12, px: 1.75, py: 1.1, borderBottom: '1px solid var(--line)', bgcolor: 'var(--sunk)', whiteSpace: 'nowrap' as const };
const tdSx = { px: 1.75, py: 1.25, borderBottom: '1px solid var(--line-2)', fontSize: 13, fontVariantNumeric: 'tabular-nums' };

function Section({ title, note, children, testid }: { title: string; note?: string; children: React.ReactNode; testid?: string }) {
  return <Box sx={boxSx} data-testid={testid}>
    <Box sx={headSx}><Typography sx={{ fontWeight: 500, flex: 1, fontSize: 13.5 }}>{title}</Typography>
      {note && <Typography sx={{ fontSize: 12, color: 'var(--ink-3)' }}>{note}</Typography>}</Box>
    {children}
  </Box>;
}

function Head({ name, about, line }: { name: string; about: AgentAbout | null; line?: string }) {
  return <Box data-testid="agent-head" sx={{ border: '1px solid var(--line)', borderRadius: '14px', px: 2.25, py: 2 }}>
    <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{name}</Typography>
    {about ? <>
      <Typography sx={{ color: 'var(--ink-2)', fontSize: 13, mt: 0.25 }}>Owns: {about.owns}.</Typography>
      <Typography sx={{ color: 'var(--ink-2)', fontSize: 13 }}>Never: {about.never}.</Typography>
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
        {about.tools.map((t) => <Box component="span" key={t.name} data-testid="agent-perm" sx={{ fontSize: 11.5, color: 'var(--ink-2)', border: '1px solid var(--line)', borderRadius: '6px', px: 0.9, py: '1px' }}>{t.does}</Box>)}
      </Box>
      <Typography sx={{ color: 'var(--ink-3)', fontSize: 12, mt: 1 }}>
        {about.may_call.length ? `May call: ${about.may_call.join(', ')}.` : 'Calls no other agent.'}
      </Typography>
    </> : line && <Typography sx={{ color: 'var(--ink-2)', fontSize: 13, mt: 0.25 }}>{line}</Typography>}
  </Box>;
}

function Limits({ rows }: { rows: Array<[string, string]> }) {
  return <Box data-testid="agent-limits" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '1px', bgcolor: 'var(--line-2)' }}>
    {rows.map(([k, v]) => <Box key={k} sx={{ bgcolor: 'var(--bg)', px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', gap: 1.25, fontSize: 13 }}>
      <Box component="span" sx={{ color: 'var(--ink-3)' }}>{k}</Box><Box component="b" sx={{ fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{v}</Box>
    </Box>)}
  </Box>;
}

function Waiting() {
  return <Box component="button" type="button" data-testid="agent-waiting" onClick={() => useSimpleSidebar.getState().showPlaceholder('action-items')}
    sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%', textAlign: 'left', border: '1px solid var(--line)', borderRadius: '12px', px: 1.75, py: 1.4, fontSize: 13, color: 'var(--ink-2)', bgcolor: 'var(--sunk)', cursor: 'pointer', font: 'inherit', '&:hover': { borderColor: 'var(--ink-4)' } }}>
    <span>What waits on a person — drafts to publish, definitions to review — is in <b>Action items</b>.</span>
  </Box>;
}

/** Closed weeks of the routes an agent answers: S15's admin read, released by its rule, never estimated. */
function RecentWork({ agent, usage }: { agent: 'data' | 'catalog'; usage: UsageWeek[] | null | undefined }) {
  const routes = ROUTES_OF[agent];
  const note = 'Closed weeks · workspace admins only';
  if (usage === undefined) return <Section title="Recent work" note={note}><Typography sx={{ p: 2, fontSize: 13, color: 'var(--ink-3)' }}>Loading…</Typography></Section>;
  if (usage === null) {
    return <Section title="Recent work" note={note} testid="agent-work-admin-only">
      <Typography sx={{ p: 2, fontSize: 13, color: 'var(--ink-3)' }}>Only a workspace admin sees the workspace's counts.</Typography>
    </Section>;
  }
  return <Section title="Recent work" note={note} testid="agent-work">
    {usage.length === 0
      ? <Typography sx={{ p: 2, fontSize: 13, color: 'var(--ink-3)' }}>No closed week has been counted yet.</Typography>
      : <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr><Box component="th" sx={thSx}>Week of</Box>
          {routes.map((r) => <React.Fragment key={r.route}><Box component="th" sx={{ ...thSx, textAlign: 'right' }}>{r.label}</Box><Box component="th" sx={{ ...thSx, textAlign: 'right' }}>Cost</Box></React.Fragment>)}
        </tr></thead>
        <tbody>{usage.map((w) => {
          const shown = !w.withheld && w.breakdown && !w.breakdown.withheld;
          return <tr key={w.week} data-testid="agent-work-week">
            <Box component="td" sx={tdSx}>{weekOf(w.week)}</Box>
            {shown
              ? routes.map((r) => {
                const g = w.breakdown!.groups.find((x) => x.value === r.route);
                return <React.Fragment key={r.route}><Box component="td" sx={{ ...tdSx, textAlign: 'right' }}>{n(g ? g.asks : 0)}</Box><Box component="td" sx={{ ...tdSx, textAlign: 'right' }}>{usd(g ? g.cost_usd : 0)}</Box></React.Fragment>;
              })
              : <Box component="td" colSpan={routes.length * 2} sx={{ ...tdSx, color: 'var(--ink-3)' }}>
                {w.withheld ? 'Not enough people asking yet' : 'Withheld: a route that week had fewer than three people asking'}
              </Box>}
          </tr>;
        })}</tbody>
      </Box>}
    <Typography sx={{ px: 2, py: 1.25, fontSize: 12, color: 'var(--ink-3)', borderTop: '1px solid var(--line-2)' }}>
      Asks the router sent to this agent's routes, and what answering them cost. Time per query and refusals by agent aren't counted yet.
    </Typography>
  </Section>;
}

function DiscoverRuns({ connections }: { connections: PipelineAgents['discover']['connections'] }) {
  const [runs, setRuns] = React.useState<Record<string, IntrospectionRun[] | 'failed'>>({});
  React.useEffect(() => {
    let live = true;
    connections.forEach((c) => {
      listIntrospectionRuns(c.id, 10)
        .then((r) => { if (live) setRuns((s) => ({ ...s, [c.id]: r })); })
        .catch(() => { if (live) setRuns((s) => ({ ...s, [c.id]: 'failed' })); });
    });
    return () => { live = false; };
  }, [connections]);
  if (!connections.length) {
    return <Section title="Runs" testid="discover-runs"><Typography sx={{ p: 2, fontSize: 13, color: 'var(--ink-3)' }}>No warehouse is connected yet. Connect one in Sources to run discovery.</Typography></Section>;
  }
  return <>{connections.map((c) => {
    const got = runs[c.id];
    return <Section key={c.id} title={`Runs · ${c.name}`} note={c.provider} testid="discover-runs">
      {got === undefined ? <Typography sx={{ p: 2, fontSize: 13, color: 'var(--ink-3)' }}>Loading…</Typography>
        : got === 'failed' ? <Typography sx={{ p: 2, fontSize: 13, color: 'var(--ink-3)' }}>The runs could not be read.</Typography>
          : got.length === 0 ? <Typography sx={{ p: 2, fontSize: 13, color: 'var(--ink-3)' }}>No runs yet.</Typography>
            : <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['Created', 'Kind', 'Status', 'Tables', 'Drafts', 'Queries', 'Cost'].map((h, i) => <Box component="th" key={h} sx={{ ...thSx, textAlign: i > 2 ? 'right' : 'left' }}>{h}</Box>)}</tr></thead>
              <tbody>{got.map((r) => {
                const res = r.result || {};
                const queries = (res as any).queries;
                return <tr key={r.id} data-testid="discover-run">
                  <Box component="td" sx={tdSx}>{day(r.created_at)}</Box>
                  <Box component="td" sx={tdSx}>{res.mode === 'discovery' ? 'Discovery' : r.status === 'completed' ? 'Cold start' : '—'}</Box>
                  <Box component="td" sx={tdSx}>{r.status === 'running' ? `Running · ${r.stage || 'starting'}` : r.status === 'completed' ? 'Done' : r.status === 'failed' ? 'Failed' : 'Waiting to start'}</Box>
                  <Box component="td" sx={{ ...tdSx, textAlign: 'right' }}>{r.table_count ? n(r.table_count) : '—'}</Box>
                  <Box component="td" sx={{ ...tdSx, textAlign: 'right' }}>{Array.isArray(res.drafts) ? n(res.drafts.length) : '—'}</Box>
                  <Box component="td" sx={{ ...tdSx, textAlign: 'right' }}>{typeof queries === 'number' ? n(queries) : '—'}</Box>
                  <Box component="td" sx={{ ...tdSx, textAlign: 'right' }}>{typeof res.cost?.cost_usd === 'number' ? usd(res.cost.cost_usd) : '—'}</Box>
                </tr>;
              })}</tbody>
            </Box>}
    </Section>;
  })}</>;
}

/**
 * The Agents page of the simplified app (#1517, S17 of #1500): one tab per pipeline agent — what the agent registry
 * says it owns and may use, its recent work in counts (S15's admin read; an admin's
 * alone), its limits as the server configures them, and what waits on a person.
 * Today's Agents page (objective agents, parked) is untouched.
 */
export default function SimpleAgentsPage() {
  const [tab, setTab] = React.useState<AgentTab>('data');
  const [agents, setAgents] = React.useState<PipelineAgents | 'failed' | null>(null);
  const [usage, setUsage] = React.useState<UsageWeek[] | null | undefined>(undefined);
  const [usageFailed, setUsageFailed] = React.useState(false);
  React.useEffect(() => {
    let live = true;
    getPipelineAgents().then((a) => { if (live) setAgents(a); }).catch(() => { if (live) setAgents('failed'); });
    getRouteUsage().then((u) => { if (live) setUsage(u); }).catch(() => { if (live) setUsageFailed(true); });
    return () => { live = false; };
  }, []);

  const body = () => {
    if (agents === null) return <Typography sx={{ fontSize: 13, color: 'var(--ink-3)' }}>Loading…</Typography>;
    if (agents === 'failed') return <Typography sx={{ fontSize: 13, color: 'var(--ink-3)' }}>The agents could not be read. Try again in a moment.</Typography>;
    const work = (a: 'data' | 'catalog') => (usageFailed
      ? <Section title="Recent work"><Typography sx={{ p: 2, fontSize: 13, color: 'var(--ink-3)' }}>The counts could not be read.</Typography></Section>
      : <RecentWork agent={a} usage={usage} />);
    if (tab === 'data') {
      const l = agents.data.limits;
      return <>
        <Head name="Data agent" about={agents.data.about} />
        <Waiting />
        {work('data')}
        <Section title="Limits" testid="data-limits">
          <Limits rows={[
            ['Rows per extract', n(l.rows_per_extract)],
            ['Rows when a question names no limit', n(l.rows_when_unlimited)],
            ['Most rows a query may ask for', n(l.rows_most_asked)],
            ['Time per query, built-in warehouse', l.bundled_seconds_per_query === null ? 'Not a valid setting' : secs(l.bundled_seconds_per_query)],
            ...l.warehouses.flatMap((w): Array<[string, string]> => [
              [`Time per query, ${w.name}`, w.seconds_per_query === null ? 'Not a valid setting' : secs(w.seconds_per_query)],
              [`Rows per query, ${w.name}`, n(w.rows_per_query)],
            ]),
          ]} />
        </Section>
      </>;
    }
    if (tab === 'discover') {
      const l = agents.discover.limits;
      return <>
        <Head name="Discover agent" about={null} line="Explores a connected warehouse and lands what it proposes as drafts in the Semantic layer. A person publishes them." />
        <Waiting />
        <DiscoverRuns connections={agents.discover.connections} />
        <Section title="Limits" testid="discover-limits">
          <Limits rows={[
            ['Queries for candidate metrics', n(l.candidate_queries)],
            ['Queries for checking and labelling', n(l.labelling_queries)],
            ['Time per query', secs(l.seconds_per_query)],
          ]} />
        </Section>
      </>;
    }
    return <>
      <Head name="Catalog agent" about={agents.catalog.about} />
      <Waiting />
      {work('catalog')}
    </>;
  };

  return <Box data-testid="simple-agents" sx={{ flex: 1, minHeight: 0, overflow: 'auto', bgcolor: 'var(--bg)' }}>
    <Box sx={{ maxWidth: 1080, mx: 'auto', px: { xs: 2, md: 4 }, pt: 4, pb: 6, display: 'flex', flexDirection: 'column', gap: 2.75 }}>
      <Box>
        <Typography component="h1" sx={{ fontSize: 24, fontWeight: 600, letterSpacing: '-.01em', mb: 0.5 }}>Agents</Typography>
        <Typography sx={{ color: 'var(--ink-3)', fontSize: 13.5, maxWidth: '70ch' }}>The agents behind every answer. Chat picks the right one for you; this is where you see their work and their limits.</Typography>
      </Box>
      <Box role="tablist" aria-label="Agents" sx={{ display: 'flex', gap: '2px', borderBottom: '1px solid var(--line)', flexWrap: 'wrap' }}>
        {TABS.map((t) => <Box component="button" type="button" role="tab" key={t.id} aria-selected={tab === t.id} data-testid={`agents-tab-${t.id}`} onClick={() => setTab(t.id)}
          sx={{ px: 1.5, pt: 1, pb: 1.4, fontSize: 13.5, font: 'inherit', bgcolor: 'transparent', border: 0, cursor: 'pointer', mb: '-1px',
            color: tab === t.id ? 'var(--ink)' : 'var(--ink-3)', fontWeight: tab === t.id ? 500 : 400,
            borderBottom: `2px solid ${tab === t.id ? 'var(--ink)' : 'transparent'}`, '&:hover': { color: 'var(--ink)' } }}>{t.label}</Box>)}
      </Box>
      <Box role="tabpanel" sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>{body()}</Box>
    </Box>
  </Box>;
}
